import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartFrame, { TOOLTIP_STYLE } from './ChartFrame';
import { fmtNumber } from '../derive';
import { EmptyState } from '../states';
import { stateName } from '../usStates';

// Drivers: acquisition trend for the range plus the supply picture by
// state (active vs total) pulled from the coverage snapshot.
export default function CanvasDrivers({ series = [], totalInRange = 0, activeDrivers = 0, totalDrivers = 0, rangeLabel = '', coverage }) {
  const hasSeries = series.some((row) => (row.count || 0) > 0);
  const byState = useMemo(
    () =>
      [...(coverage?.states || [])]
        .filter((row) => row.drivers > 0)
        .sort((a, b) => b.drivers - a.drivers)
        .slice(0, 10)
        .map((row) => ({
          state: row.state,
          name: stateName(row.state),
          active: row.activeDrivers,
          inactive: Math.max(0, row.drivers - row.activeDrivers),
        })),
    [coverage]
  );
  const activePct = totalDrivers ? Math.round((activeDrivers / totalDrivers) * 100) : 0;

  return (
    <div className="grid h-full min-h-0 gap-3 laptop:grid-cols-[minmax(0,1.1fr)_minmax(320px,1fr)]">
      <ChartFrame
        title="Driver signups"
        subtitle={`By day · ${rangeLabel}`}
        right={<span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtNumber(totalInRange)} in range</span>}
        footer={
          <span>
            Signup date is <code>driver.createdAt</code>, set once at registration.
          </span>
        }
      >
        {hasSeries ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="count" name="Signups" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="No driver signups" description="Try widening the date range." />
        )}
      </ChartFrame>

      <ChartFrame
        title="Supply by state"
        subtitle={`${fmtNumber(activeDrivers)} of ${fmtNumber(totalDrivers)} drivers can take orders (${activePct}%)`}
        footer={
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-600" /> Active</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-zinc-300" /> Not able to take orders</span>
          </span>
        }
      >
        {byState.length === 0 ? (
          <EmptyState title="No driver addresses" description="Drivers need a home state on file to appear here." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byState} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
              <YAxis dataKey="state" type="category" tick={{ fontSize: 10, fill: '#71717a' }} width={28} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: 'rgba(16,185,129,0.06)' }}
                labelFormatter={(label) => stateName(label)}
              />
              <Bar dataKey="active" name="Active" stackId="s" fill="#059669" isAnimationActive={false} />
              <Bar dataKey="inactive" name="Inactive" stackId="s" fill="#d4d4d8" radius={[0, 4, 4, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartFrame>
    </div>
  );
}
